import { Avatar } from '../atoms/Avatar';
import { ChatWidgetRenderer } from '../widgets/ChatWidget';
import { MarkdownText } from '../molecules/MarkdownText';
import { InternalMessage } from '../molecules/InternalMessage';
import { FeedbackButton } from './FeedbackButton';
import type { ChatMessage } from '../../types/coach';

interface MessageGroupProps {
  messages: ChatMessage[];
  role: 'user' | 'assistant' | 'tool-use' | 'tool-result';
  showAvatar?: boolean;
  showTimestamp?: boolean;
  userId?: string;
  sessionId?: string;
  showToolBlocks?: boolean;
}

export function MessageGroup({ messages, role, showAvatar = true, showTimestamp = false, userId, sessionId, showToolBlocks = true }: MessageGroupProps) {
  const isUser = role === 'user';
  const isInternal = role === 'tool-use' || role === 'tool-result';
  const latestMessage = messages[messages.length - 1];

  const renderTextWidget = (widget: { text: string }, isUser: boolean) => {
    if (isUser) {
      return (
        <div className="text-sm leading-relaxed whitespace-pre-wrap text-white">
          {widget.text}
        </div>
      );
    }
    return (
      <MarkdownText className="text-sm leading-relaxed text-text-primary">
        {widget.text}
      </MarkdownText>
    );
  };

  // For internal messages, show them like assistant messages with avatar
  if (isInternal) {
    // Hide internal messages if showToolBlocks is false
    if (!showToolBlocks) {
      return null;
    }
    
    return (
      <div className="flex gap-3 flex-row">
        {/* Avatar */}
        {showAvatar && (
          <div className="flex-shrink-0">
            <Avatar type="assistant" size="md" />
          </div>
        )}

        {/* Messages */}
        <div className="flex flex-col gap-2 items-start flex-1 min-w-0">
          {messages.map((message, index) => (
            <div key={message.id} className="fade-in w-full" style={{ animationDelay: `${index * 50}ms` }}>
              <InternalMessage message={message} />
            </div>
          ))}

          {/* Timestamp */}
          {showTimestamp && (
            <div className="text-xs text-text-muted mt-1 px-2 text-left">
              {latestMessage.timestamp.toLocaleTimeString([], { 
                hour: '2-digit', 
                minute: '2-digit' 
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
      {/* Avatar */}
      {showAvatar && (
        <div className="flex-shrink-0">
          <Avatar type={role} size="md" />
        </div>
      )}

      {/* Messages */}
      <div className={`flex flex-col gap-2 ${isUser ? 'items-end' : 'items-start'} flex-1 min-w-0`}>
        {messages.map((message, index) => (
          <div key={message.id} className="fade-in w-full" style={{ animationDelay: `${index * 50}ms` }}>
            {/* Internal Messages */}
            {message.internal && showToolBlocks && <InternalMessage message={message} />}
            {!message.internal && (
              /* Regular Messages */
              <div className={`message-bubble ${
                isUser ? 'user' : 'assistant'
              } p-4 ${index > 0 ? 'mt-1' : ''} ${!isUser ? 'w-full' : ''}`}>
                <div className={`space-y-3 ${isUser ? 'text-white' : 'text-text-primary'}`}>
                  {message.widgets?.map((widget, widgetIndex) => (
                    <div key={`widget-${message.id}-${widgetIndex}`}>
                      {widget.type === 'text' ? renderTextWidget(widget, isUser) : (
                        <div className={isUser ? 'opacity-95 transform scale-95' : ''}>
                          <ChatWidgetRenderer widget={widget} />
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {/* Add feedback button for assistant messages */}
                  {!isUser && userId && (
                    <div className="flex justify-end mt-2 pt-2 border-t border-border-separator">
                      <FeedbackButton
                        messageId={message.id}
                        userId={userId}
                        sessionId={sessionId}
                        message={message}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}

        {/* Timestamp */}
        {showTimestamp && (
          <div className={`text-xs text-text-muted mt-1 px-2 ${isUser ? 'text-right' : 'text-left'}`}>
            {latestMessage.timestamp.toLocaleTimeString([], { 
              hour: '2-digit', 
              minute: '2-digit' 
            })}
          </div>
        )}
      </div>
    </div>
  );
}